import { useSortableItem } from '@/hooks/useSortableItem';
import { stripHtml } from '@/utils/helpers/courses.helper';
import { TrashIcon } from '@/utils/helpers/icons.helper';
import { t } from 'i18next';
import React from 'react';
import styled from 'styled-components';

interface QuizQuestionThumbnailProps {
  questionIndex: number;
  questionText: string;
  isSelected: boolean;
  onClick: (index: number) => void;
  onDelete: (index: number) => void;
  id: string;
  isAnyDragging?: boolean;
}

const ThumbnailCard = styled.div<{ $isSelected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 195px;
  height: 144px;
  flex-shrink: 0;
  box-sizing: border-box;
  padding: var(--xsmall-padding);
  border: 2px solid
    ${({ $isSelected }) =>
      $isSelected ? 'var(--red-main)' : 'var(--gray-dark)'};
  border-radius: var(--xsmall-border-radius);
  background: var(--white-main);
  cursor: pointer;

  &:hover .thumbnail-delete {
    opacity: 1;
    pointer-events: auto;
  }
`;

const PreviewText = styled.p`
  align-self: stretch;
  margin: 0;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  color: var(--black-main);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const NumberBadge = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  margin-top: auto;
  width: 163px;
  height: 78px;
  padding: var(--default-padding);
  border-radius: var(--xxsmall-border-radius);
  background: ${({ $isSelected }) =>
    $isSelected ? 'var(--red-main-20)' : 'var(--beige-main)'};
  color: var(--black-main);
`;

const DeleteButton = styled.button.attrs({ type: 'button' })`
  position: absolute;
  top: var(--xsmall-padding);
  right: var(--xsmall-padding);
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s;
`;

export const QuizQuestionThumbnail: React.FC<QuizQuestionThumbnailProps> = ({
  questionIndex,
  questionText,
  isSelected,
  onClick,
  onDelete,
  id,
  isAnyDragging = false,
}) => {
  const { setNodeRef, dragHandleProps, isDragging, style } = useSortableItem({
    id,
    style: { zIndex: 1 },
  });

  const handleSelect = () => {
    if (isAnyDragging) return;
    onClick(questionIndex);
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDelete(questionIndex);
  };

  return (
    <ThumbnailCard
      ref={setNodeRef}
      style={{
        ...style,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      $isSelected={isSelected}
      onClick={handleSelect}
      {...dragHandleProps}
    >
      <DeleteButton
        className="thumbnail-delete"
        onClick={handleDelete}
        title={t('content.delete.question')}
      >
        <TrashIcon />
      </DeleteButton>
      <PreviewText>{stripHtml(questionText)}</PreviewText>
      <NumberBadge $isSelected={isSelected}>{questionIndex + 1}</NumberBadge>
    </ThumbnailCard>
  );
};
